import { defineStore } from "pinia";
import { ref, type Ref } from "vue";

interface LessonVoteEntry {
  hasVoted: Ref<boolean>;
  isLoading: Ref<boolean>;
  /** Cache request */
  fetchPromise: Promise<void> | null;
}

function buildKey(lessonUuid: string, isMoeLesson: boolean): string {
  return `${isMoeLesson ? "moe" : "cg"}:${lessonUuid}`;
}

/**
 * Shared per-lesson upvote state, keyed by lesson domain + uuid.
 *
 * Backs useModuleUpvote() so every mounted consumer (editor header,
 * assign/copy wait-modal prompt, student end page, ...) for the same lesson
 * reads and writes the same hasVoted/isLoading Ref — toggling upvote in one
 * place immediately reflects in every other mounted place.
 */
export const useModuleUpvoteStore = defineStore("moduleUpvote", () => {
  const votesByKey = new Map<string, LessonVoteEntry>();

  function getEntry(lessonUuid: string, isMoeLesson: boolean): LessonVoteEntry {
    const key = buildKey(lessonUuid, isMoeLesson);
    let entry = votesByKey.get(key);
    if (!entry) {
      entry = { hasVoted: ref(false), isLoading: ref(false), fetchPromise: null };
      votesByKey.set(key, entry);
    }
    return entry;
  }

  return { getEntry };
});
